import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Mind Bender — FTUE demo (3 essais).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HUE_HEX } from './params';
const RED = 'red';
const BLUE = 'blue';
const GREEN = 'green';
const STAR = '★';
const CIRCLE = '●';
const TRI = '▲';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'mind_bender.demo.step1_title',
        hintKey: 'mind_bender.demo.step1_hint',
        matrixSize: 2,
        cells: [
            { shape: STAR, hue: RED, count: 1 },
            { shape: CIRCLE, hue: RED, count: 1 },
            { shape: STAR, hue: BLUE, count: 1 },
            null,
        ],
        candidates: [
            { id: 'c1', cell: { shape: CIRCLE, hue: BLUE, count: 1 } },
            { id: 'c2', cell: { shape: STAR, hue: BLUE, count: 1 } },
            { id: 'c3', cell: { shape: TRI, hue: BLUE, count: 1 } },
            { id: 'c4', cell: { shape: CIRCLE, hue: RED, count: 1 } },
        ],
        correctId: 'c1',
        ruleHint: 'forme par colonne',
    },
    {
        idx: 2,
        titleKey: 'mind_bender.demo.step2_title',
        hintKey: 'mind_bender.demo.step2_hint',
        matrixSize: 2,
        cells: [
            { shape: STAR, hue: RED, count: 1 },
            { shape: CIRCLE, hue: RED, count: 1 },
            { shape: STAR, hue: GREEN, count: 1 },
            null,
        ],
        candidates: [
            { id: 'c1', cell: { shape: CIRCLE, hue: GREEN, count: 1 } },
            { id: 'c2', cell: { shape: STAR, hue: GREEN, count: 1 } },
            { id: 'c3', cell: { shape: CIRCLE, hue: RED, count: 1 } },
            { id: 'c4', cell: { shape: TRI, hue: GREEN, count: 1 } },
        ],
        correctId: 'c1',
        ruleHint: 'forme par colonne · couleur par ligne',
    },
    {
        idx: 3,
        titleKey: 'mind_bender.demo.step3_title',
        hintKey: 'mind_bender.demo.step3_hint',
        matrixSize: 2,
        cells: [
            { shape: TRI, hue: RED, count: 1 },
            { shape: STAR, hue: RED, count: 1 },
            { shape: TRI, hue: BLUE, count: 1 },
            null,
        ],
        candidates: [
            { id: 'c1', cell: { shape: STAR, hue: BLUE, count: 1 } },
            { id: 'c2', cell: { shape: TRI, hue: BLUE, count: 1 } },
            { id: 'c3', cell: { shape: STAR, hue: RED, count: 1 } },
            { id: 'c4', cell: { shape: CIRCLE, hue: BLUE, count: 1 } },
        ],
        correctId: 'c1',
        ruleHint: 'forme par colonne · couleur par ligne',
    },
];
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [pickedId, setPickedId] = useState(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const correct = pickedId === step.correctId;
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setPickedId(null);
    };
    const renderCell = (cell, size, state) => {
        if (!cell) {
            return (_jsx("div", { className: [
                    'flex items-center justify-center border-2 border-cyan-network bg-cyan-surface',
                    size === 'sm' ? 'h-[60px] w-[60px]' : 'h-[68px] w-[68px]',
                ].join(' '), children: _jsx("span", { className: "font-display text-[20px] text-cyan-text-secondary", children: "?" }) }));
        }
        const cls = state === 'correct'
            ? 'border-2 border-cyan-core bg-cyan-core/10'
            : state === 'incorrect'
                ? 'border-2 border-cyan-media bg-cyan-media/10'
                : 'border border-cyan-border-faint bg-cyan-surface';
        return (_jsx("div", { className: [
                'flex items-center justify-center',
                size === 'sm' ? 'h-[60px] w-[60px] text-[18px]' : 'h-[68px] w-[68px] text-[22px]',
                cls,
            ].join(' '), children: _jsx("span", { style: { color: HUE_HEX[cell.hue] }, children: cell.shape }) }));
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('mind_bender.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsx("p", { className: "font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-text-faint", children: t('mind_bender.rule_hint', { hint: step.ruleHint }) }), _jsx("div", { className: "grid gap-s2 border border-cyan-border-faint bg-cyan-surface p-s3", style: { gridTemplateColumns: `repeat(${step.matrixSize}, minmax(0, 1fr))` }, children: step.cells.map((c, i) => (_jsx("div", { children: renderCell(c, 'md') }, i))) }), _jsx("div", { className: "flex flex-wrap justify-center gap-s2", children: step.candidates.map((c) => {
                    const isCorrect = c.id === step.correctId;
                    const isPicked = pickedId === c.id;
                    const isFb = pickedId !== null;
                    const state = isFb && isCorrect ? 'correct' : isFb && isPicked ? 'incorrect' : undefined;
                    return (_jsx("button", { type: "button", onClick: () => setPickedId(c.id), disabled: pickedId !== null, "aria-label": `Candidat ${c.cell.shape} ${c.cell.hue}`, className: "transition active:scale-95 disabled:cursor-not-allowed", children: renderCell(c.cell, 'sm', state) }, c.id));
                }) }), pickedId !== null && (_jsx("p", { className: [
                    'text-[13px]',
                    correct ? 'text-cyan-core' : 'text-cyan-media',
                ].join(' '), children: correct
                    ? t('mind_bender.demo.feedback_correct')
                    : t('mind_bender.demo.feedback_incorrect') })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [pickedId !== null && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map